import { useEffect, useMemo, useState } from 'react'
import Avatar from '../components/Avatar'
import {
  useAlertesCouchages, useAssignations, useAssigner,
  useChambres, useComptes, useCouples, useCreateLit, useDeleteLit,
  useNuits, usePersonnes, useUpdateLit,
} from '../api/hooks'
import type {
  AlerteCouchage, Chambre, Lit, Nuit, Personne, TypeChambre, TypeLit,
} from '../api/types'
import { LIT_ICON, LIT_LABEL, LIT_SPOTS } from '../api/types'

const ETAGES: Record<number, { nom: string; icon: string }> = {
  0: { nom: 'Sous-sol', icon: '🏠' },
  1: { nom: '1er étage', icon: '🏡' },
  2: { nom: '2e étage', icon: '🏔️' },
  3: { nom: 'Studio annexe', icon: '🌿' },
}

const TYPE_LABEL: Record<TypeChambre, string> = {
  double: 'Couple', solo: 'Solo / mixte', enfants: 'Enfants & ados',
}
const TYPE_ICON: Record<TypeChambre, string> = {
  double: '💑', solo: '🛏️', enfants: '🧒',
}

const sortAlpha = (a: Personne, b: Personne) =>
  a.prenom.localeCompare(b.prenom, 'fr', { sensitivity: 'base' })

export default function Couchages() {
  const chambresQ = useChambres()
  const personnesQ = usePersonnes()
  const comptesQ = useComptes()
  const couplesQ = useCouples()
  const nuitsQ = useNuits()
  const assignationsQ = useAssignations()
  const alertesQ = useAlertesCouchages()
  const assigner = useAssigner()

  const nuits = useMemo<Nuit[]>(() => nuitsQ.data?.nuits ?? [], [nuitsQ.data])
  const [selectedNuitId, setSelectedNuitId] = useState<number | null>(null)
  const [showAlertes, setShowAlertes] = useState(false)
  /** Panneau « Choisir une personne » : id de la chambre cible, ou null si fermé */
  const [pickerChambreId, setPickerChambreId] = useState<number | null>(null)

  useEffect(() => {
    if (selectedNuitId == null && nuits.length > 0) setSelectedNuitId(nuits[0].id)
  }, [nuits, selectedNuitId])

  // === Index ===
  // IMPORTANT : on filtre p.present pour EXCLURE les refus (qui sont dans
  // l'array `presences` avec present=false, refus=true — voir API /nuits).
  // Sans ce filtre, une personne en "Ne vient pas" serait comptée comme
  // présente et apparaîtrait dans "candidats sans chambre".
  const presenceParNuit = useMemo(() => {
    const m = new Map<string, boolean>()
    nuitsQ.data?.presences.forEach(p => {
      if (p.present) m.set(`${p.personne_id}-${p.nuit_id}`, true)
    })
    return m
  }, [nuitsQ.data])

  // Index : (personne_id, nuit_id) → tentatif (true si "peut-être / provisoire")
  const tentatifParNuit = useMemo(() => {
    const m = new Map<string, boolean>()
    nuitsQ.data?.presences.forEach(p => {
      if (p.present) m.set(`${p.personne_id}-${p.nuit_id}`, !!p.tentatif)
    })
    return m
  }, [nuitsQ.data])

  const presencesNuitsParPersonne = useMemo(() => {
    const m = new Map<number, Set<number>>()
    nuitsQ.data?.presences.forEach(p => {
      if (!p.present) return // refus ou autre ligne non-présente : ignorer
      let s = m.get(p.personne_id)
      if (!s) { s = new Set(); m.set(p.personne_id, s) }
      s.add(p.nuit_id)
    })
    return m
  }, [nuitsQ.data])

  const personneById = useMemo(() => {
    const m = new Map<number, Personne>()
    personnesQ.data?.forEach(p => m.set(p.id, p))
    return m
  }, [personnesQ.data])

  const compteById = useMemo(() => {
    const m = new Map<number, string>()
    comptesQ.data?.forEach(c => m.set(c.id, c.nom))
    return m
  }, [comptesQ.data])

  const assignParPersonne = useMemo(() => {
    const m = new Map<number, number | null>()
    if (selectedNuitId == null) return m
    assignationsQ.data?.forEach(a => {
      if (a.nuit_id === selectedNuitId) m.set(a.personne_id, a.chambre_id)
    })
    return m
  }, [assignationsQ.data, selectedNuitId])

  const occupantsParChambre = useMemo(() => {
    const m = new Map<number, number[]>()
    if (selectedNuitId == null) return m
    assignationsQ.data?.forEach(a => {
      if (a.nuit_id !== selectedNuitId || a.chambre_id == null) return
      if (!presenceParNuit.get(`${a.personne_id}-${selectedNuitId}`)) return
      const arr = m.get(a.chambre_id) ?? []
      arr.push(a.personne_id)
      m.set(a.chambre_id, arr)
    })
    return m
  }, [assignationsQ.data, selectedNuitId, presenceParNuit])

  /** Personnes inscrites cette nuit, non encore assignées, **triées A→Z** */
  const candidatsLibres = useMemo<Personne[]>(() => {
    if (!personnesQ.data || selectedNuitId == null) return []
    return personnesQ.data
      .filter(p => p.actif)
      .filter(p => presenceParNuit.get(`${p.id}-${selectedNuitId}`))
      .filter(p => assignParPersonne.get(p.id) == null)
      .sort(sortAlpha)
  }, [personnesQ.data, presenceParNuit, selectedNuitId, assignParPersonne])

  const alertesPourNuit = useMemo(() => {
    if (!alertesQ.data) return []
    return alertesQ.data.filter(a => a.nuit_id === selectedNuitId)
  }, [alertesQ.data, selectedNuitId])

  // Nuits avec « trop de monde » (capacité dépassée OU personne sans chambre).
  // Les peut-être / provisoires ont une chambre attribuée donc ils comptent déjà
  // dans la capacité côté backend → l'alerte couvre tentatifs inclus.
  const nuitsEnSurcapacite = useMemo(() => {
    const s = new Set<number>()
    alertesQ.data?.forEach(a => {
      if ((a.type === 'sur_capacite' || a.type === 'non_assigne') && a.nuit_id != null) {
        s.add(a.nuit_id)
      }
    })
    return s
  }, [alertesQ.data])

  // Fermer le picker à l'ESC
  useEffect(() => {
    if (pickerChambreId == null) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPickerChambreId(null) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [pickerChambreId])

  if (!chambresQ.data || !personnesQ.data || !comptesQ.data || nuitsQ.isLoading) {
    return <p className="text-ocean-700 italic">Chargement…</p>
  }

  const chambres = chambresQ.data
  const capaciteTotale = chambres.reduce((s, c) => s + c.capacite, 0)
  const nbPresentsCetteNuit = personnesQ.data!.filter(
    p => p.actif && selectedNuitId != null && presenceParNuit.get(`${p.id}-${selectedNuitId}`),
  ).length
  const nbAssignesCetteNuit = Array.from(occupantsParChambre.values())
    .reduce((s, arr) => s + arr.length, 0)

  const chambresParEtage = chambres.reduce<Record<number, Chambre[]>>((acc, c) => {
    (acc[c.etage] ??= []).push(c); return acc
  }, {})
  const etagesOrdered = Object.keys(chambresParEtage).map(Number).sort((a, b) => a - b)

  const pickerChambre = pickerChambreId ? chambres.find(c => c.id === pickerChambreId) ?? null : null

  const handleAssigner = (personne_id: number, chambre_id: number | null) => {
    // Le placement se fait TOUJOURS pour la seule nuit affichée — l'utilisateur
    // a besoin de pouvoir piloter chambre par chambre (cf. correction itérative
    // des couchages par jour).
    if (selectedNuitId == null) return
    assigner.mutate({ personne_id, chambre_id, nuit_id: selectedNuitId })
  }

  return (
    <div className="space-y-4 sm:space-y-5">
      {/* === Hero : capacité === */}
      <section className="card overflow-hidden">
        <div className="bg-gradient-to-br from-ocean-500 to-ocean-700 text-white px-4 sm:px-6 py-4 sm:py-5 flex items-start gap-3 sm:gap-4">
          <div className="text-3xl sm:text-4xl shrink-0">🛏️</div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg sm:text-2xl font-bold">Capacité de couchage</h2>
            <p className="text-ocean-100 text-sm mt-0.5">
              La maison Etchehoun dispose de <strong className="text-white">{capaciteTotale} couchages</strong> répartis sur <strong className="text-white">{chambres.length} chambres</strong>.
            </p>
          </div>
        </div>
        <div className="px-4 sm:px-6 py-3 bg-ocean-50/60 border-t border-ocean-100 flex flex-wrap gap-2 items-center text-xs sm:text-sm">
          <span className="text-ocean-700">
            <strong>{nbAssignesCetteNuit}</strong> / {capaciteTotale} place{nbAssignesCetteNuit > 1 ? 's' : ''} utilisée{nbAssignesCetteNuit > 1 ? 's' : ''} cette nuit ·{' '}
            <strong>{nbPresentsCetteNuit}</strong> présent{nbPresentsCetteNuit > 1 ? 's' : ''}
            {alertesPourNuit.length > 0 && (
              <> · <strong className="text-coral-600">{alertesPourNuit.length} alerte{alertesPourNuit.length > 1 ? 's' : ''}</strong></>
            )}
          </span>
        </div>
      </section>

      {/* === Bloc d'info pédagogique === */}
      <section className="card overflow-hidden border-l-4 border-sunset-500 bg-gradient-to-br from-sand-50 to-orange-50">
        <div className="p-4 sm:p-5">
          <h3 className="font-bold text-sand-800 text-base sm:text-lg flex items-center gap-2 mb-2">
            <span aria-hidden>💡</span>
            <span>Comment fonctionne la répartition&nbsp;?</span>
          </h3>
          <p className="text-sm text-sand-800 leading-relaxed">
            La répartition ci-dessous est une{' '}
            <strong className="text-coral-700">simple proposition</strong> générée
            <strong> automatiquement</strong> à chaque fois qu'une personne coche ou décoche
            une nuit dans l'onglet <strong>Nuitées</strong> — à partir des présences saisies
            et des couples connus.
            <br />
            <strong className="text-coral-700">Chacun et chacune peut la modifier librement</strong>{' '}
            à tout moment pour se positionner sur le lit ou la chambre de son choix, parmi
            ceux qui sont disponibles.
          </p>
          <ul className="mt-3 space-y-1 text-sm text-sand-800">
            <li className="flex items-start gap-2">
              <span aria-hidden className="w-5 shrink-0 text-center">🛏️</span>
              <span>Clique sur <strong>« + Libre »</strong> dans un emplacement vide pour t'y attribuer (ou y placer quelqu'un d'autre).</span>
            </li>
            <li className="flex items-start gap-2">
              <span aria-hidden className="w-5 shrink-0 text-center text-coral-500">✕</span>
              <span>Clique sur la <strong className="text-coral-700">croix rouge</strong> d'un occupant pour libérer la place.</span>
            </li>
            <li className="flex items-start gap-2">
              <span aria-hidden className="w-5 shrink-0 text-center">🌙</span>
              <span>Change de <strong>nuit</strong> dans le sélecteur ci-dessous pour ajuster une nuit en particulier.</span>
            </li>
          </ul>
        </div>
      </section>

      {/* === Sélecteur de nuit (sticky) — toutes les nuits visibles d'un coup === */}
      <section className="card p-3 sm:p-4 sticky top-0 sm:top-[49px] z-10">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
          <h3 className="font-bold text-ocean-900 text-sm shrink-0">
            Nuit affichée
            <span className="ml-2 text-xs font-normal text-ocean-700">({nuits.length} nuits au total)</span>
          </h3>
          <span className="ml-auto text-[11px] text-ocean-600/80 italic">
            Les modifications de chambre s'appliquent à la <strong>nuit affichée</strong> uniquement.
          </span>
        </div>
        <div className="grid grid-cols-7 md:grid-cols-14 gap-1 sm:gap-1.5">
          {nuits.map(n => {
            const isSel = n.id === selectedNuitId
            const isOver = nuitsEnSurcapacite.has(n.id)
            const [debut, fin] = n.libelle.split('→').map(s => s.trim())
            const debutCompact = debut.split(' ').slice(0, 2).join(' ')
            const finCompact = fin.split(' ').slice(0, 2).join(' ')
            // Style : surcapacité (rouge) > sélectionné (ocean) > normal (gris-bleu)
            const cls = isSel
              ? (isOver
                  ? 'bg-gradient-to-br from-coral-500 to-coral-600 text-white shadow-soft ring-2 ring-coral-300'
                  : 'bg-gradient-ocean text-white shadow-soft')
              : (isOver
                  ? 'bg-coral-50 text-coral-800 hover:bg-coral-100 border border-coral-300'
                  : 'bg-ocean-50 text-ocean-700 hover:bg-ocean-100')
            return (
              <button
                key={n.id}
                onClick={() => setSelectedNuitId(n.id)}
                title={isOver ? `${n.libelle} — ⚠ trop de monde (provisoires compris)` : n.libelle}
                className={`relative flex flex-col items-center justify-center py-1.5 px-1 rounded-lg text-center transition focus:outline-none focus:ring-2 focus:ring-ocean-400 ${cls}`}
              >
                <span className="text-[10px] sm:text-[11px] font-bold leading-tight whitespace-nowrap">
                  {debutCompact}
                </span>
                <span className={`text-[8px] sm:text-[9px] whitespace-nowrap leading-tight ${isSel ? 'opacity-90' : 'opacity-60'}`}>
                  → {finCompact}
                </span>
                {isOver && (
                  <span
                    aria-hidden
                    className="absolute -top-1.5 -right-1 text-[10px] bg-coral-600 text-white rounded-full px-1 leading-tight shadow-sm"
                  >⚠</span>
                )}
              </button>
            )
          })}
        </div>
      </section>

      {/* === Alertes (collapsible) === */}
      {alertesPourNuit.length > 0 && (
        <section className="card overflow-hidden">
          <button
            onClick={() => setShowAlertes(v => !v)}
            className="w-full px-4 sm:px-5 py-3 flex items-center justify-between text-left hover:bg-coral-50/40 transition"
          >
            <h3 className="font-bold text-coral-700 flex items-center gap-2">
              <span>⚠</span>
              <span>{alertesPourNuit.length} alerte{alertesPourNuit.length > 1 ? 's' : ''} pour cette nuit</span>
            </h3>
            <span className="text-coral-700">{showAlertes ? '▾' : '▸'}</span>
          </button>
          {showAlertes && (
            <ul className="px-4 sm:px-5 pb-3 space-y-1.5 anim-in">
              {alertesPourNuit.map((a, i) => <AlerteRow key={i} alerte={a} personneById={personneById} />)}
            </ul>
          )}
        </section>
      )}

      {/* === Non-assignés présents === */}
      {candidatsLibres.length > 0 && (
        <section className="card p-4 sm:p-5 border-2 border-coral-200 bg-coral-50/30 anim-in">
          <h3 className="font-bold text-coral-800 mb-2 flex items-center gap-2">
            <span>🧳</span>
            <span>
              {candidatsLibres.length} personne{candidatsLibres.length > 1 ? 's' : ''} sans chambre cette nuit
            </span>
          </h3>
          <p className="text-xs text-coral-700/80 mb-3">Choisis une chambre pour chaque voyageur :</p>
          <div className="flex flex-wrap gap-2">
            {candidatsLibres.map(p => (
              <PersonnePill
                key={p.id}
                personne={p}
                chambres={chambres}
                compteNom={compteById.get(p.compte_id)}
                onAssigner={(cid) => handleAssigner(p.id, cid)}
              />
            ))}
          </div>
        </section>
      )}

      {/* === Étages === */}
      {etagesOrdered.map(etage => {
        const lst = chambresParEtage[etage]
        const etageCap = lst.reduce((s, c) => s + c.capacite, 0)
        const etageOcc = lst.reduce((s, c) => s + (occupantsParChambre.get(c.id)?.length ?? 0), 0)
        const meta = ETAGES[etage] ?? { nom: `Étage ${etage}`, icon: '📍' }
        return (
          <section key={etage} className="space-y-2 sm:space-y-3">
            <div className="flex items-baseline justify-between px-1">
              <h3 className="text-base sm:text-lg font-bold text-ocean-900 flex items-center gap-2">
                <span aria-hidden>{meta.icon}</span>
                <span>{meta.nom}</span>
              </h3>
              <span className="text-xs text-ocean-700 font-medium">
                {etageOcc}/{etageCap} place{etageCap > 1 ? 's' : ''}
              </span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {lst.map(ch => (
                <ChambreCard
                  key={ch.id}
                  chambre={ch}
                  occupants={(occupantsParChambre.get(ch.id) ?? []).map(id => personneById.get(id)!).filter(Boolean)}
                  tentatifParPersonne={(pid: number) =>
                    selectedNuitId != null ? tentatifParNuit.get(`${pid}-${selectedNuitId}`) ?? false : false
                  }
                  compteById={compteById}
                  presencesNuitsParPersonne={presencesNuitsParPersonne}
                  onRetirer={(personne_id) => handleAssigner(personne_id, null)}
                  onOpenPicker={() => setPickerChambreId(ch.id)}
                  couplesData={couplesQ.data ?? []}
                />
              ))}
            </div>
          </section>
        )
      })}

      {/* === MODAL : choisir une personne pour la chambre === */}
      {pickerChambre && (
        <BedSlotPickerModal
          chambre={pickerChambre}
          candidats={candidatsLibres}
          compteById={compteById}
          presencesNuitsParPersonne={presencesNuitsParPersonne}
          onPick={(pid) => { handleAssigner(pid, pickerChambre.id); setPickerChambreId(null) }}
          onCancel={() => setPickerChambreId(null)}
        />
      )}
    </div>
  )
}

/* ============ Chambre card ============ */
function ChambreCard({
  chambre, occupants, tentatifParPersonne, compteById, presencesNuitsParPersonne,
  onRetirer, onOpenPicker, couplesData,
}: {
  chambre: Chambre
  occupants: Personne[]
  tentatifParPersonne: (id: number) => boolean
  compteById: Map<number, string>
  presencesNuitsParPersonne: Map<number, Set<number>>
  onRetirer: (id: number) => void
  onOpenPicker: () => void
  couplesData: Array<{ personne_a_id: number; personne_b_id: number }>
}) {
  const surCapacite = occupants.length > chambre.capacite

  const idsOccupants = new Set(occupants.map(o => o.id))
  const couplesIciIds = new Set<number>()
  couplesData.forEach(c => {
    if (idsOccupants.has(c.personne_a_id) && idsOccupants.has(c.personne_b_id)) {
      couplesIciIds.add(c.personne_a_id); couplesIciIds.add(c.personne_b_id)
    }
  })

  const totalSlots = Math.max(chambre.capacite, occupants.length)
  const gridCols =
    chambre.capacite === 1 ? 'grid-cols-1' :
    chambre.capacite >= 3 ? 'grid-cols-1' :
    'grid-cols-2'

  return (
    <div className={`card p-3 sm:p-4 card-hover ${surCapacite ? 'ring-2 ring-coral-300' : ''}`}>
      <header className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide font-bold text-ocean-700/80">
            <span>{TYPE_ICON[chambre.type]}</span>
            <span>{TYPE_LABEL[chambre.type]}</span>
          </div>
          <h4 className="font-bold text-ocean-900 leading-tight">
            {chambre.nom.split('·').pop()?.trim() ?? chambre.nom}
          </h4>
        </div>
        <CapacityBadge total={chambre.capacite} occ={occupants.length} sur={surCapacite} />
      </header>

      <p className="text-[11px] text-ocean-700/70 mb-3 line-clamp-2">{chambre.description}</p>

      <div className={`grid ${gridCols} gap-2`}>
        {Array.from({ length: totalSlots }).map((_, idx) => {
          const occ = occupants[idx]
          if (occ) {
            const inCouple = couplesIciIds.has(occ.id)
            const isTentatif = tentatifParPersonne(occ.id)
            const nbNuits = presencesNuitsParPersonne.get(occ.id)?.size ?? 0
            return (
              <BedSlotOccupied
                key={occ.id}
                personne={occ}
                inCouple={inCouple}
                isTentatif={isTentatif}
                compteNom={compteById.get(occ.compte_id)}
                nbNuits={nbNuits}
                onRetirer={() => onRetirer(occ.id)}
                excess={idx >= chambre.capacite}
              />
            )
          }
          return (
            <button
              key={`empty-${idx}`}
              type="button"
              onClick={onOpenPicker}
              className="bed-slot-empty"
              aria-label={`Ajouter une personne dans ${chambre.nom}`}
            >
              <span aria-hidden className="text-base">+</span>
              <span>Libre</span>
            </button>
          )
        })}
      </div>

      <LitsManager chambre={chambre} />
    </div>
  )
}

/* ============ Gestion des lits (CRUD) ============ */
function LitsManager({ chambre }: { chambre: Chambre }) {
  const [open, setOpen] = useState(false)
  const createLit = useCreateLit()
  const updateLit = useUpdateLit()
  const deleteLit = useDeleteLit()
  const busy = createLit.isPending || updateLit.isPending || deleteLit.isPending

  const lits = [...(chambre.lits ?? [])].sort((a, b) => a.ordre - b.ordre || a.id - b.id)
  const nbSpots = lits.reduce((s, l) => s + (LIT_SPOTS[l.type] ?? 0), 0)

  return (
    <div className="mt-3 pt-3 border-t border-ocean-100/70">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between text-[11px] uppercase tracking-wide font-bold text-ocean-700 hover:text-ocean-900 transition"
        aria-expanded={open}
      >
        <span className="flex items-center gap-1.5">
          <span aria-hidden>🪑</span>
          <span>Lits ({lits.length}) — {nbSpots} place{nbSpots > 1 ? 's' : ''}</span>
        </span>
        <span aria-hidden>{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="mt-2 space-y-1.5 anim-in">
          {lits.length === 0 && (
            <p className="text-[11px] text-ocean-500 italic">
              Aucun lit dans cette chambre.
            </p>
          )}
          {lits.map(lit => (
            <LitRow
              key={lit.id}
              lit={lit}
              busy={busy}
              onUpdate={(type) => updateLit.mutate({ id: lit.id, body: { type } })}
              onDelete={() => {
                if (window.confirm(`Supprimer ce ${LIT_LABEL[lit.type].toLowerCase()} ?`)) {
                  deleteLit.mutate(lit.id)
                }
              }}
            />
          ))}

          <div className="flex flex-wrap gap-1.5 pt-1.5">
            <span className="text-[11px] text-ocean-600/80 self-center mr-1">+</span>
            {(['lit_simple', 'lit_double', 'lit_superpose'] as TypeLit[]).map(t => (
              <button
                key={t}
                type="button"
                disabled={busy}
                onClick={() => createLit.mutate({ chambre_id: chambre.id, type: t })}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-ocean-200 hover:border-ocean-400 hover:bg-ocean-50 text-[11px] font-semibold text-ocean-800 transition disabled:opacity-50"
                title={`Ajouter un ${LIT_LABEL[t].toLowerCase()}`}
              >
                <span aria-hidden>{LIT_ICON[t]}</span>
                <span>{LIT_LABEL[t]}</span>
              </button>
            ))}
          </div>

          {nbSpots !== chambre.capacite && (
            <p className="text-[10px] text-coral-600 italic">
              ⚠ Discrepance temporaire : {nbSpots} places listées vs capacité {chambre.capacite} (rafraîchissement en cours…).
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function LitRow({
  lit, busy, onUpdate, onDelete,
}: {
  lit: Lit
  busy: boolean
  onUpdate: (type: TypeLit) => void
  onDelete: () => void
}) {
  return (
    <div className="flex items-center gap-1.5 bg-ocean-50/60 border border-ocean-100 rounded-md px-2 py-1.5">
      <span aria-hidden className="text-sm">{LIT_ICON[lit.type]}</span>
      <select
        value={lit.type}
        disabled={busy}
        onChange={e => onUpdate(e.target.value as TypeLit)}
        className="flex-1 min-w-0 text-[11px] sm:text-xs font-semibold bg-white border border-ocean-200 rounded px-1.5 py-0.5 text-ocean-900 focus:outline-none focus:ring-1 focus:ring-ocean-400 disabled:opacity-50"
        aria-label="Type de lit"
      >
        <option value="lit_simple">Lit simple (1 pers.)</option>
        <option value="lit_double">Lit double (2 pers.)</option>
        <option value="lit_superpose">Lit superposé (2 pers.)</option>
      </select>
      <button
        type="button"
        disabled={busy}
        onClick={onDelete}
        className="shrink-0 w-6 h-6 rounded-md text-coral-600 hover:text-white hover:bg-coral-500 transition disabled:opacity-50 flex items-center justify-center text-xs font-bold"
        aria-label={`Supprimer ${LIT_LABEL[lit.type]}`}
        title={`Supprimer ${LIT_LABEL[lit.type]}`}
      >
        ✕
      </button>
    </div>
  )
}

function CapacityBadge({ total, occ, sur }: { total: number; occ: number; sur: boolean }) {
  const isFull = occ >= total && !sur
  return (
    <div className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${
      sur ? 'bg-coral-100 text-coral-700' :
      isFull ? 'bg-ocean-100 text-ocean-700' :
      'bg-sand-100 text-sand-800'
    }`}>
      <span>{occ}/{total}</span>
      {isFull && <span aria-hidden>✓</span>}
      {sur && <span aria-hidden>⚠</span>}
    </div>
  )
}

function BedSlotOccupied({
  personne, inCouple, isTentatif, compteNom, nbNuits, onRetirer, excess,
}: {
  personne: Personne
  inCouple: boolean
  isTentatif: boolean
  compteNom?: string
  nbNuits: number
  onRetirer: () => void
  excess: boolean
}) {
  const baseCls = inCouple ? 'bed-slot-occupied-couple' : 'bed-slot-occupied'
  const tentatifCls = isTentatif
    ? 'border-dashed border-2 border-amber-400 bg-amber-50/70 opacity-90'
    : ''
  return (
    <div
      className={`${baseCls} ${tentatifCls} ${excess ? 'bed-slot-disabled' : ''}`}
      title={isTentatif ? 'Provisoire — peut se retirer si trop de monde' : undefined}
    >
      <Avatar personne_id={personne.id} prenom={personne.prenom} avatar_version={personne.avatar_version} size="md" />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm text-ocean-900 truncate flex items-center gap-1">
          <span className="truncate">{personne.prenom}</span>
          {inCouple && <span className="text-coral-500 shrink-0" title="Couple">💞</span>}
          {isTentatif && (
            <span
              className="shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-amber-200 text-amber-800 border border-amber-400"
              title="Provisoire — peut se retirer si trop de monde"
            >↔ Prov.</span>
          )}
        </div>
        <div className="text-[10px] text-ocean-700/70 truncate">
          {compteNom} · {nbNuits} nuit{nbNuits > 1 ? 's' : ''}
        </div>
      </div>
      <button
        type="button"
        onClick={onRetirer}
        className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-coral-500 hover:bg-coral-600 text-white text-base leading-none flex items-center justify-center shadow-md transition focus:outline-none focus:ring-2 focus:ring-coral-300 active:scale-95"
        aria-label={`Retirer ${personne.prenom}`}
        title="Retirer cette personne de la chambre"
      >×</button>
    </div>
  )
}

/* ============ Modal "Choisir une personne" ============ */
function BedSlotPickerModal({
  chambre, candidats, compteById, presencesNuitsParPersonne, onPick, onCancel,
}: {
  chambre: Chambre
  candidats: Personne[]
  compteById: Map<number, string>
  presencesNuitsParPersonne: Map<number, Set<number>>
  onPick: (id: number) => void
  onCancel: () => void
}) {
  const [filter, setFilter] = useState('')
  const filtered = filter.trim()
    ? candidats.filter(p => p.prenom.toLowerCase().includes(filter.toLowerCase().trim()))
    : candidats

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-ocean-900/40 z-50 anim-in"
        onClick={onCancel}
        aria-hidden
      />

      {/* Bottom sheet (mobile) / Centered modal (sm+) */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="picker-title"
        className="
          fixed z-50 bg-white shadow-2xl flex flex-col
          inset-x-0 bottom-0 max-h-[85vh] rounded-t-2xl
          sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2
          sm:max-w-md sm:w-full sm:max-h-[80vh] sm:rounded-2xl
          anim-in
        "
      >
        {/* Header */}
        <header className="px-4 sm:px-5 py-3 sm:py-4 border-b border-ocean-100 flex items-start justify-between gap-2 rounded-t-2xl bg-white shrink-0">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wide font-bold text-ocean-700/80 flex items-center gap-1">
              <span>{TYPE_ICON[chambre.type]}</span>
              <span>Choisir une personne</span>
            </div>
            <h3 id="picker-title" className="font-bold text-ocean-900 text-base sm:text-lg leading-tight">
              {chambre.nom}
            </h3>
            <p className="text-[11px] text-ocean-700 mt-0.5">
              {candidats.length} personne{candidats.length > 1 ? 's' : ''} inscrite{candidats.length > 1 ? 's' : ''} · classées A → Z
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="w-9 h-9 -m-1 rounded-full bg-coral-50 hover:bg-coral-100 text-coral-600 hover:text-coral-700 text-xl leading-none flex items-center justify-center shrink-0 focus:outline-none focus:ring-2 focus:ring-coral-300"
            aria-label="Fermer"
          >×</button>
        </header>

        {/* Search */}
        {candidats.length > 5 && (
          <div className="px-4 sm:px-5 pt-3 shrink-0">
            <input
              type="search"
              autoFocus
              value={filter}
              onChange={e => setFilter(e.target.value)}
              placeholder="🔍 Rechercher un prénom…"
              className="input py-2"
            />
          </div>
        )}

        {/* Liste */}
        <div className="overflow-y-auto flex-1 px-2 py-2 sm:px-3 sm:py-3">
          {candidats.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-2">😴</div>
              <p className="text-sm text-ocean-700 font-medium">
                Personne d'inscrit pour cette nuit n'est disponible.
              </p>
              <p className="text-xs text-ocean-600 mt-1">
                Vérifie les présences dans l'onglet <strong>Nuitées</strong>.
              </p>
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-sm text-ocean-500 italic py-6">
              Aucun prénom ne correspond à « {filter} ».
            </p>
          ) : (
            <ul className="space-y-1">
              {filtered.map(p => {
                const nbNuits = presencesNuitsParPersonne.get(p.id)?.size ?? 0
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => onPick(p.id)}
                      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-ocean-50 focus:bg-ocean-50 rounded-lg text-left transition focus:outline-none focus:ring-2 focus:ring-ocean-300"
                    >
                      <Avatar personne_id={p.id} prenom={p.prenom} avatar_version={p.avatar_version} size="md" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-ocean-900 truncate">{p.prenom}</div>
                        <div className="text-xs text-ocean-600 truncate">
                          {compteById.get(p.compte_id)} · {nbNuits} nuit{nbNuits > 1 ? 's' : ''}
                        </div>
                      </div>
                      <span className="text-ocean-400 text-xl shrink-0">→</span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        {/* Footer */}
        <footer className="px-4 sm:px-5 py-3 border-t border-ocean-100 bg-ocean-50/30 shrink-0 flex items-center justify-between rounded-b-2xl sm:rounded-b-2xl">
          <span className="text-xs text-ocean-600">
            {filtered.length} sur {candidats.length}
          </span>
          <button
            type="button"
            onClick={onCancel}
            className="btn-ghost btn-sm"
          >
            Annuler
          </button>
        </footer>
      </div>
    </>
  )
}

/* ============ Pill (non-assigné) ============ */
function PersonnePill({
  personne, chambres, compteNom, onAssigner,
}: {
  personne: Personne
  chambres: Chambre[]
  compteNom?: string
  onAssigner: (chambre_id: number) => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1.5 bg-white border border-coral-300 hover:border-coral-500 rounded-full pl-1 pr-2.5 py-1 shadow-soft"
      >
        <Avatar personne_id={personne.id} prenom={personne.prenom} avatar_version={personne.avatar_version} size="xs" />
        <span className="text-sm font-medium text-ocean-900">{personne.prenom}</span>
        {compteNom && <span className="text-[10px] text-ocean-600">({compteNom})</span>}
        <span className="text-coral-600">+</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 mt-1 bg-white border border-ocean-100 rounded-lg shadow-card py-1 z-50 min-w-[220px] anim-in max-h-64 overflow-y-auto">
            {chambres.map(c => (
              <button
                key={c.id}
                type="button"
                onClick={() => { onAssigner(c.id); setOpen(false) }}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-ocean-50 flex items-center gap-1.5"
              >
                <span className="opacity-60">{TYPE_ICON[c.type]}</span>
                <span className="truncate">{c.nom}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

/* ============ Alerte ============ */
function AlerteRow({ alerte, personneById }: {
  alerte: AlerteCouchage
  personneById: Map<number, Personne>
}) {
  const isError = alerte.severity === 'error'
  return (
    <li className={`flex items-start gap-2 text-sm rounded-lg px-3 py-2 ${
      isError ? 'bg-coral-50 text-coral-800' : 'bg-sand-100/60 text-sand-800'
    }`}>
      <span className="text-base shrink-0">{isError ? '🚨' : '⚠'}</span>
      <div className="flex-1 min-w-0">
        <div className="font-medium">{alerte.message}</div>
        {alerte.personne_ids.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {alerte.personne_ids.map(pid => {
              const p = personneById.get(pid)
              if (!p) return null
              return (
                <div key={pid} className="flex items-center gap-1 bg-white/70 rounded-full pl-0.5 pr-2 py-0.5">
                  <Avatar personne_id={pid} prenom={p.prenom} avatar_version={p.avatar_version} size="xs" />
                  <span className="text-xs">{p.prenom}</span>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </li>
  )
}
